import type {
  CreateRepresentativeInput,
  Representative,
} from "../types/representative";
import {
  formatPhoneForApiPayload,
} from "../utils/validation/legalEntityValidation";
import { apiRequest } from "./apiClient";

type BackendRepresentativeItem = {
  readonly id: string;
  readonly entityName?: string | null;
  readonly name?: string | null;
  readonly title?: string | null;
  readonly company?: string | null;
  readonly streetAddress?: string | null;
  readonly city?: string | null;
  readonly state?: string | null;
  readonly zip?: string | null;
  readonly phone?: string | null;
  readonly email?: string | null;
  readonly address?: string | null;
  readonly status?: string | null;
};

type BackendRepresentativesResponse = {
  readonly data: BackendRepresentativeItem[];
  readonly meta?: unknown;
};

type BackendCreateRepresentativeResponse = {
  readonly data?: BackendRepresentativeItem;
};

export function mergeRepresentativeAddress(
  representative: Pick<
    Representative,
    "streetAddress" | "city" | "state" | "zip" | "address"
  >
): string {
  const street = representative.streetAddress?.trim();
  const city = representative.city?.trim();
  const state = representative.state?.trim();
  const zip = representative.zip?.trim();
  const hasStructured = Boolean(street || city || state || zip);
  if (hasStructured) {
    const stateZip = [state, zip].filter(Boolean).join(" ");
    return [street, city, stateZip].filter(Boolean).join(", ");
  }
  return representative.address?.trim() ?? "";
}

export function formatRepresentativeCityStateZip(
  representative: Pick<Representative, "city" | "state" | "zip">
): string {
  const city = representative.city?.trim() ?? "";
  const state = representative.state?.trim() ?? "";
  const zip = representative.zip?.trim() ?? "";
  const stateZip = [state, zip].filter(Boolean).join(" ");
  if (city && stateZip) {
    return `${city}, ${stateZip}`;
  }
  if (city) {
    return city;
  }
  return stateZip;
}

const mapItem = (item: BackendRepresentativeItem): Representative => {
  const raw: Representative = {
    id: item.id,
    name: item.name?.trim() || "Unknown",
    title: item.title?.trim() || "",
    company: (item.company ?? item.entityName ?? "").trim(),
    streetAddress: item.streetAddress?.trim() || null,
    city: item.city?.trim() || null,
    state: item.state?.trim() || null,
    zip: item.zip?.trim() || null,
    phone: item.phone?.trim() || null,
    email: item.email?.trim() || null,
    address: item.address?.trim() || null,
    status: item.status ?? undefined,
  };
  const merged = mergeRepresentativeAddress(raw);
  return {
    ...raw,
    address: merged.length > 0 ? merged : raw.address,
  };
};

export const listRepresentatives = async (): Promise<Representative[]> => {
  const response = await apiRequest(
    "/representatives?status=Active&limit=200&offset=0"
  );
  const json = (await response.json()) as BackendRepresentativesResponse;
  if (!Array.isArray(json.data)) {
    return [];
  }

  return json.data.map(mapItem);
};

export const createRepresentative = async (
  input: CreateRepresentativeInput
): Promise<Representative> => {
  const response = await apiRequest("/representatives", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: input.name,
      title: input.title,
      company: input.company,
      streetAddress: input.streetAddress,
      city: input.city,
      state: input.state,
      zip: input.zip,
      phone: formatPhoneForApiPayload(input.phone),
      email: input.email,
    }),
  });

  const json = (await response.json()) as BackendCreateRepresentativeResponse;
  if (!json.data) {
    throw new Error("Failed to create representative");
  }
  return mapItem(json.data);
};
